// ui-core/user/gacUserPersistence.ts
import { atom } from "nanostores";
import type { UserOutputType } from "../GACApi4/schemas";

const STORAGE_KEY = "studio-assistant:gac-user";
let initialized = false;

const $gacUser = atom<UserOutputType | null>(null);

/**
 * Initialize user persistence:
 * - hydrate from localStorage (best-effort)
 * - subscribe to cross-tab changes
 */
export function initUserPersistence() {
  if (initialized || typeof window === "undefined") return;
  initialized = true;

  // 1️⃣ Hydrate from localStorage
  try {
    const raw = localStorage.getItem(STORAGE_KEY);

    if (raw) {
      const user = JSON.parse(raw) as UserOutputType;
      $gacUser.set(user);
    } else {
      // console.log('No user found in localstorage');
    }
  } catch {
    console.warn("Failed to hydrate user from storage");
  }

  // 2️⃣ Cross-tab / cross-window sync
  window.addEventListener("storage", (e) => {
    if (e.key !== STORAGE_KEY) return;

    if (!e.newValue) {
      $gacUser.set(null);
      return;
    }

    try {
      const user = JSON.parse(e.newValue) as UserOutputType;
      $gacUser.set(user);
    } catch {
      console.warn("Failed to sync user from storage event");
      $gacUser.set(null);
    }
  });
}

/**
 * Sets the user state and persists it
 * Used by AuthIsland and any islands that update the user
 */
export function setUser(user: UserOutputType | null) {
  $gacUser.set(user);
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(user));
  } catch {
    console.warn("Failed to persist user");
  }
}

/**
 * Clears the user state and removes persisted data
 * Called on logout
 */
export function clearUser() {
  $gacUser.set(null);
  localStorage.removeItem(STORAGE_KEY);
}

export function getUser() {
  return $gacUser.get();
}

// direct access to the store for react hooks and such
export function getUserStore() {
  return $gacUser;
}

export function isLoggedIn() {
  return !!$gacUser.get();
}

/**
 * Subscribe to user changes.
 * Returns a disposer you can call to stop listening.
 */
export function onUserChange(callback: (user: UserOutputType | null) => void) {
  return $gacUser.listen(callback);
}
